import {
  // TActions,
  TError,
  TFieldConfig,
  TFormField,
  TSubmitType,
} from "../../types/common";
import dayjs from "dayjs";
import {
  beautify,
  objectWithUnsetKeys,
  removeUnnecessaryFields,
  returnArray,
  returnObject,
} from "../../utils/helpers";
import SelectInput from "./SelectInput";
import { DateInput } from "./DateInput";
import { Wrapper } from "../styled/Styled";
import ErrorSlate from "./ErrorSlate";
import {
  CButton,
  CFormInput,
  CFormTextarea,
  CInputGroup,
  CInputGroupText,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import Assets from "./Assets";
import { CommonModal } from "./Modals";
import ModalForm from "./ModalForm";
import React, { useRef, useState } from "react";
import { ChildWrapper } from "./FormSection";
import DetailsCard from "./DetailsCards";
import MapFormComponent from "./map/MapFormComponent";
import { Button, Checkbox, ConfigProvider, TimePicker } from "antd";
import { MODAL } from "../../utils/constants";
import FileInput from "./FileInput";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import styled from "styled-components";
import { Box, Grid, TextField } from "@mui/material";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import MultiNumberInput from "./MultiNumberInput";

import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

interface ModalFieldWrapperProps {
  item: any;
  form: any;
  showModal: any;
  setShowModal: (modalState: any) => void;
  addItem: (fieldName: string, multiple?: boolean) => void;
  onEditItem: (item: any, index?: any) => void;
  onRemoveItem: (key: any, index: any) => void;
  onRemoveObjectItem: (key: any) => void;
  detailsView: (item: any) => any;
  error: TError;
  options: any;
  updateFormValue: (fieldName: string, value: any) => void;
  updateFormValues: (values: any) => void;
  setError: (error: TError) => void;
  label: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ModalFieldWrapper: React.FC<ModalFieldWrapperProps> = ({
  item,
  form,
  showModal,
  setShowModal,
  addItem,
  onEditItem,
  onRemoveItem,
  onRemoveObjectItem,
  detailsView,
  error,
  options,
  updateFormValue,
  updateFormValues,
  setError,
  label,
  onChange,
}) => {
  return (
    <Wrapper>
      {!(item?.props?.multiple === false && form[item?.field_name]) && (
        <ConfigProvider
          theme={{
            token: { colorPrimary: "green" },
          }}
        >
          <Button
            type="primary"
            className="font13"
            icon={item?.props?.icon}
            onClick={() => setShowModal({ show: true })}
            disabled={item?.props?.disabled}
          >
            {label}
          </Button>
        </ConfigProvider>
      )}
      <CommonModal
        title={label}
        show={showModal.show}
        onHide={() => setShowModal({ show: false })}
        onOkayPress={() => addItem(item?.field_name, item?.props?.multiple)}
        okayType="submit"
        body={
          <ChildWrapper>
            <ModalForm
              fields={item?.fields}
              field={item?.field_name}
              error={error}
              form={form}
              onChange={onChange}
              options={options}
              updateFormValue={updateFormValue}
              updateFormValues={updateFormValues}
              multiple={item?.props?.multiple}
              data={showModal.data}
              setError={setError}
            />
          </ChildWrapper>
        }
        error={error}
        formId="generic-modal-form"
      />
      <div style={{ marginTop: "0.5rem" }}>
        {item?.props?.multiple === false && form?.[item?.field_name] ? (
          <DetailsCard
            viewData={detailsView(form?.[item?.field_name])}
            onEdit={() => onEditItem(item)}
            onDelete={() => onRemoveObjectItem(item?.field_name)}
          />
        ) : (
          (form?.[item?.field_name] || []).map((it: any, idx: any) => (
            <DetailsCard
              key={idx}
              viewData={detailsView(it)}
              onEdit={() => onEditItem(item, idx)}
              onDelete={() => onRemoveItem(item?.field_name, idx)}
            />
          ))
        )}
      </div>
    </Wrapper>
  );
};

const FormFields = ({
  fields,
  sectionFields,
  error,
  form,
  onChange,
  updateFormValue,
  updateFormValues,
  options,
  labelVariables,
  submitType,
  disabledDates,
  setError,
}: TFormField): any => {
  const [showModal, setShowModal] = useState<{
    show: boolean;
    data?: any;
    action?: TSubmitType;
  }>({ show: false });

  const [notFoundOption, setNotFoundOption] = useState(false);

  // Validation function for a single field
  const validateField = (field: TFieldConfig, value: any): string | null => {
    const { validation, props } = field;

    // Skip validation for disabled fields (auto-filled from external data)
    if (props?.disabled) return null;

    if (!validation) return null;

    // Check pattern validation
    if (validation.pattern) {
      const pattern = validation.pattern.value;
      if (value && !pattern.test(value)) {
        return validation.pattern.message;
      }
    }

    // Check minLength validation
    if (validation.minLength) {
      if (value && value.length < validation.minLength.value) {
        return validation.minLength.message;
      }
    }

    // Check maxLength validation
    if (validation.maxLength) {
      if (value && value.length > validation.maxLength.value) {
        return validation.maxLength.message;
      }
    }

    // Check min validation (for numbers)
    if (validation.min !== undefined) {
      if (
        value !== null &&
        value !== undefined &&
        Number(value) < validation.min.value
      ) {
        return validation.min.message;
      }
    }

    // Check max validation (for numbers)
    if (validation.max !== undefined) {
      if (
        value !== null &&
        value !== undefined &&
        Number(value) > validation.max.value
      ) {
        return validation.max.message;
      }
    }

    // Check custom validation
    if (validation.custom) {
      const result = validation.custom(value);
      if (typeof result === "string") {
        return result;
      }
      if (result === false) {
        return `Invalid ${field.label || field.field_name}`;
      }
    }

    return null;
  };

  const addItem = (field_name: string, multiple?: boolean) => {
    const item = form?.data;
    if (!item) return;
    delete form?.data;

    let field_data = form?.[field_name];

    if (showModal.action === "edit") {
      if (multiple === false) {
        field_data = { ...field_data, ...item };
      } else {
        field_data = field_data.map((it: any, idx: number) =>
          idx === item.index ? item : it,
        );
      }
    } else {
      if (multiple === false) {
        field_data = { ...field_data, ...item };
      } else {
        field_data = field_data ? [...field_data, item] : [item];
      }
    }

    updateFormValue(field_name, removeUnnecessaryFields(field_data));
    setShowModal({ show: false });
  };

  const removeFilePath = (form_data: any) => {
    const filePathRegex =
      /^(?:[a-zA-Z]:\\|\/)?(?:[a-zA-Z0-9_-]+[\\/])*[a-zA-Z0-9._-]+$/;
    Object.keys(form_data).forEach((key) => {
      if (
        typeof form_data[key] === "string" &&
        !filePathRegex.test(form_data[key])
      ) {
        form_data[key] = "";
      }
    });
    return form_data;
  };

  const onEditItem = (item: any, index: any = null) => {
    let form_data: any;
    if (item?.props?.multiple === false) {
      form_data = form?.[item.field_name];
    } else {
      form_data = form?.[item.field_name][index];
      form_data.index = index;
    }
    form_data = removeFilePath(form_data);

    setShowModal({
      show: true,
      data: removeUnnecessaryFields(form_data),
      action: "edit",
    });
  };

  const detailsView = (item: any) => {
    let new_item: any = {};
    Object.keys(item).map((key) => {
      if (!key.includes("-name"))
        new_item[key] =
          item[`${key}-name`] != null ? item[`${key}-name`] : item[key];
    });
    new_item = removeUnnecessaryFields(new_item);
    // Get value of key that ends with _attachments
    // const attachmentValue = Object.entries(new_item).find(([key]) =>
    //   key.endsWith("_attachments"),
    // )?.[1];
    // if (attachmentValue) {
    //   new_item = {
    //     ...new_item,
    //     // ...displayDocuments(attachmentValue),
    //   };
    // }
    // Filter out attachment fields and other fields
    // const attachmentFields = Object.keys(new_item).filter((key) =>
    //   key.startsWith("attachments"),
    // );
    // const regularFields = Object.keys(new_item).filter(
    //   (key) => !key.startsWith("attachments"),
    // );

    // // Process regular fields
    // const processedRegularFields = regularFields.reduce((acc, key) => {
    //   acc[key] = new_item[key];
    //   return acc;
    // }, {});

    // // Process attachment fields
    // const processedAttachments = attachmentFields.reduce((acc, key) => {
    //   if (new_item[key] && Array.isArray(new_item[key])) {
    //     acc[key] = new_item[key].map((attachment) => ({
    //       name: attachment.caption || "Unnamed file",
    //       url: attachment.url || "#",
    //     }));
    //   }
    //   return acc;
    // }, {});

    // // Combine processed fields
    // const processedItem = {
    //   ...processedRegularFields,
    //   attachments: processedAttachments,
    // };

    // return processedItem;
    return new_item;
  };

  const onRemoveItem = (key: any, index: any) => {
    const array = (form[key] || []).filter((_: any, idx: any) => idx !== index);
    updateFormValue(key, removeUnnecessaryFields(array));
  };

  const onRemoveObjectItem = (key: any) => {
    updateFormValues({
      [key]: null,
    });
  };

  if (!Array.isArray(fields) && !Array.isArray(sectionFields)) return null;
  const actualFields = (fields: TFieldConfig[]) => {
    return fields.map((item, idx) => {
      const {
        type,
        placeholder,
        field_name,
        value,
        label = beautify(field_name),
        description,
        labelType,
        props,
        options_name,
        options_data,
        multiple,
        visible = true,
        component,
        loading = false,
      } = item;

      if (!visible) return;

      function getPlaceholder() {
        return placeholder || `Enter ${label}`;
      }
      const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue =
          event.target.value === undefined ? null : event.target.value;

        // Validate the field
        const validationError = validateField(item, newValue);

        // Update error state with notSubmit flag to prevent toast notifications
        if (setError && validationError) {
          setError({
            ...error,
            [field_name]: validationError,
            notSubmit: true, // Prevent toast notifications for real-time validation
          });
        } else if (setError && !validationError && error?.[field_name]) {
          // Clear error if validation passes
          const newError = { ...error };
          delete newError[field_name];
          // Keep notSubmit flag if there are other errors
          if (
            Object.keys(newError).filter((k) => k !== "notSubmit").length > 0
          ) {
            newError.notSubmit = true;
          }
          setError(newError);
        }

        if (props?.parent_field) {
          updateFormValue(props?.parent_field, {
            ...form?.[props?.parent_field],
            [field_name]: newValue,
          });
          return;
        }
        if (onChange) onChange(event);
      };

      // Example modification for a text input field
      const normalValue = props?.parent_field
        ? form?.[props?.parent_field]?.[field_name]
        : form?.[field_name] || value;

      // Example modification for a select input field
      // const selectValue = props?.parent_field
      //   ? form?.[props?.parent_field]?.[field_name]
      //   : form?.[field_name];

      // // Example modification for a date input field
      // const dateValue = props?.parent_field
      //   ? form?.[props?.parent_field]?.[field_name]
      //   : form?.[field_name];

      if (type === "select") {
        return (
          <Wrapper key={idx}>
            {label ? (
              <p
                className="capitalize font12 mb-1"
                style={{ display: "flex", alignItems: "center", gap: 4 }}
              >
                {label}
                <RequiredIndicator required={props?.required} />
                <InfoTooltip description={description} />
              </p>
            ) : null}
            <SelectInputWrapper>
              <div style={{ flex: "1" }}>
                <SelectInput
                  options={(options_name && options
                    ? returnArray(options[options_name])
                    : options_data
                      ? options_data
                      : []
                  ).map((item) => ({
                    value: item.id,
                    label: item.name,
                    ...item.extraFields,
                  }))}
                  onChange={
                    (value, label) =>
                      updateFormValues
                        ? updateFormValues({
                            [field_name as string]: value,
                            [`${field_name as string}_name`]: label
                              ? label["label"]
                              : "",
                            ...objectWithUnsetKeys(label, ["value", "label"]),
                          })
                        : null
                    // updateFormValue
                    //   ? updateFormValue(field_name as string, value)
                    //   : null
                  }
                  value={
                    form[field_name]
                      ? isNaN(form[field_name as keyof typeof field_name])
                        ? form[field_name as keyof typeof field_name]
                        : form[field_name as keyof typeof field_name]
                      : value
                  }
                  label={label}
                  name={field_name}
                  error={error}
                  multiple={props?.multiple}
                  loading={loading}
                  acceptNewItems={props?.acceptNewItems}
                  setNotFoundOption={setNotFoundOption}
                  {...props}
                />
              </div>

              {item?.action_modal &&
                ((notFoundOption && props?.newLine) ||
                  form?.[item?.action_modal?.field_name]?.length > 0) && (
                  <ModalFieldWrapper
                    item={item?.action_modal}
                    form={form}
                    showModal={showModal}
                    setShowModal={setShowModal}
                    addItem={addItem}
                    onEditItem={onEditItem}
                    onRemoveItem={onRemoveItem}
                    onRemoveObjectItem={onRemoveObjectItem}
                    detailsView={detailsView}
                    error={error}
                    options={options}
                    updateFormValue={updateFormValue}
                    updateFormValues={updateFormValues}
                    setError={setError || (() => {})}
                    label={item?.action_modal?.label}
                    onChange={onChange || (() => {})}
                  />
                )}
            </SelectInputWrapper>

            {error &&
              error[field_name] &&
              typeof error[field_name] === "string" && (
                <ErrorSlate message={error[field_name] as string} />
              )}
          </Wrapper>
        );
      }

      if (type === MODAL) {
        return (
          <ModalFieldWrapper
            item={item}
            form={form}
            showModal={showModal}
            setShowModal={setShowModal}
            addItem={addItem}
            onEditItem={onEditItem}
            onRemoveItem={onRemoveItem}
            onRemoveObjectItem={onRemoveObjectItem}
            detailsView={detailsView}
            error={error}
            options={options}
            updateFormValue={updateFormValue}
            updateFormValues={updateFormValues}
            setError={setError || (() => {})}
            label={label}
            onChange={onChange || (() => {})}
          />
        );
      }

      if (type === "otp-pin-input") {
        const length = Array.isArray(fields)
          ? fields[0]?.props?.length || 4
          : 4;
        const [otp, setOtp] = useState(Array(length).fill(""));

        const inputsRef: any = useRef([]);

        // Get background variant from props (default to 'blue')
        const bgVariant = props?.bgVariant || "blue"; // 'blue' or 'white'

        // Define colors based on background
        const colorThemes = {
          blue: {
            text: "white",
            labelText: "white",
            border: "white",
            borderHover: "#e0e0e0",
            borderFocus: "#ffffff",
          },
          white: {
            text: "#000000",
            labelText: "#000000",
            border: "#d0d0d0",
            borderHover: "#1a75ba",
            borderFocus: "#1a75ba",
          },
        };

        const currentTheme = colorThemes[bgVariant];

        // Sync local OTP state with form value for auto-fill
        React.useEffect(() => {
          const formValue = form[field_name];
          if (
            formValue &&
            typeof formValue === "string" &&
            formValue.length > 0
          ) {
            const otpArray = formValue.split("").slice(0, length);
            // Pad with empty strings if needed
            while (otpArray.length < length) {
              otpArray.push("");
            }
            setOtp(otpArray);
          }
        }, [form[field_name]]);

        const handleChange = (value: any, index: any) => {
          if (!/^[0-9]?$/.test(value)) return;

          const newOtp = [...otp];
          newOtp[index] = value;
          setOtp(newOtp);

          updateFormValue
            ? updateFormValue(field_name as string, newOtp.join(""))
            : null;

          if (value && index < length - 1) {
            inputsRef.current[index + 1]?.focus();
          }
        };

        const handleKeyDown = (e: any, index: any) => {
          if (e.key === "Backspace" && !otp[index] && index > 0) {
            const newOtp = [...otp];
            newOtp[index - 1] = "";
            setOtp(newOtp);
            inputsRef.current[index - 1]?.focus();
          }
        };

        return (
          <Grid container spacing={2} alignItems="center">
            {label ? (
              <Grid item md={2}>
                <p
                  className="capitalize font12 mb-1"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    color: currentTheme.labelText,
                  }}
                >
                  {label}
                  <RequiredIndicator required={props?.required} />
                  <InfoTooltip description={description} />
                </p>
              </Grid>
            ) : null}

            <Grid item md={label ? 10 : 12}>
              <Box display="flex" gap={1}>
                {otp?.map((value, index) => (
                  <TextField
                    key={index}
                    inputRef={(el) => (inputsRef.current[index] = el)}
                    value={value}
                    variant="outlined"
                    size="small"
                    type={props?.type === "password" ? "password" : "text"}
                    onChange={(e) => handleChange(e.target.value, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    style={{ flex: 1 }}
                    inputProps={{
                      maxLength: 1,
                      style: {
                        textAlign: "center",
                        fontSize: "1.3rem",
                        borderRadius: "10px",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        "& fieldset": {
                          borderColor: currentTheme.border,
                        },
                        "&:hover fieldset": {
                          borderColor: currentTheme.borderHover,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: currentTheme.borderFocus,
                        },
                      },
                      input: {
                        color: currentTheme.text,
                      },
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        );
      }

      if (type === "tel") {
        //  const [phone, setPhone] = useState(form[field_name] || "");
        let phone = form[field_name] || "";

        const handlePhoneOnChange = (value: any) => {
          // setPhone(value);
          phone = value;

          // Remove spaces from phone number for validation
          const cleanedValue = value ? value.replace(/\s+/g, "") : "";

          // Validate the phone field using validation rules
          const validationError = validateField(item, cleanedValue);

          if (validationError) {
            setError &&
              setError({
                ...(error || {}),
                [field_name]: validationError,
                notSubmit: true,
              });
          } else if (error?.[field_name]) {
            // Only update if there was an error before
            const updatedError = { ...error };
            delete updatedError[field_name];
            // Keep notSubmit flag if there are other errors
            if (
              Object.keys(updatedError).filter(
                (k) => k !== "notSubmit" && k !== "data",
              ).length > 0
            ) {
              updatedError.notSubmit = true;
            }
            setError && setError(updatedError);
          }
          updateFormValue ? updateFormValue(field_name as string, value) : null;
        };

        // useEffect(() => {
        //   setPhone(form[field_name] || "");
        // }, [form[field_name]]);

        return (
          <Wrapper key={idx}>
            {label ? (
              <p
                className="capitalize font12 mb-1"
                style={{ display: "flex", alignItems: "center", gap: 4 }}
              >
                {label}
                <RequiredIndicator required={props?.required} />
                <InfoTooltip description={description} />
              </p>
            ) : null}
            {error &&
              error[field_name] &&
              typeof error[field_name] === "string" && (
                <ErrorSlate message={error[field_name] as string} />
              )}
            <div id={field_name}>
              <PhoneInput
                country={"ug"}
                specialLabel={""}
                value={phone}
                onChange={handlePhoneOnChange}
                placeholder="Enter phone number"
                enableSearch={true}
                containerStyle={{ width: "100%" }}
                inputClass="phone-input-custom"
                dropdownStyle={{
                  borderRadius: "8px",
                  borderColor: "#ccc",
                  boxShadow:
                    "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
                }}
              />
            </div>
          </Wrapper>
        );
      }

      if (type === "date") {
        return (
          <Wrapper key={idx}>
            {label ? (
              <p className="capitalize font12 mb-1">
                {labelType === "variable" ? labelVariables?.[label] : label}{" "}
                <RequiredIndicator required={props?.required} />
                <InfoTooltip description={item?.description} />
              </p>
            ) : null}
            {error &&
              error[field_name] &&
              typeof error[field_name] === "string" && (
                <ErrorSlate message={error[field_name] as string} />
              )}
            <DateInput
              size="small"
              fullWidth
              type={type}
              id={field_name}
              name={field_name}
              placeholder={getPlaceholder()}
              value={
                form[field_name as keyof typeof field_name]
                  ? dayjs(form[field_name as keyof typeof field_name])
                  : null
              }
              onChange={(value) => {
                if (updateFormValue)
                  updateFormValue(
                    field_name,
                    value?.format(props?.format || "YYYY-MM-DD"),
                  );
              }}
              format="MM-DD-YYYY"
              {...props}
              disabledDate={disabledDates ? disabledDates[field_name] : null}
            />
          </Wrapper>
        );
      }

      if (type === "time") {
        return (
          <Wrapper key={idx}>
            {label ? (
              <p className="capitalize font12 mb-1">
                {label} <RequiredIndicator required={props?.required} />
                <InfoTooltip description={item?.description} />
              </p>
            ) : null}
            {error &&
              error[field_name] &&
              typeof error[field_name] === "string" && (
                <ErrorSlate message={error[field_name] as string} />
              )}
            <TimePicker
              size="middle"
              style={{ width: "100%" }}
              format="HH:mm"
              placeholder={getPlaceholder()}
              value={
                form[field_name as keyof typeof field_name]
                  ? dayjs(
                      form[field_name as keyof typeof field_name],
                      "HH:mm:ss",
                    )
                  : null
              }
              onChange={(value) => {
                if (updateFormValue) {
                  const timeString = value
                    ? `${value.format("HH:mm")}:00`
                    : null;
                  updateFormValue(field_name, timeString);
                }
              }}
              {...props}
            />
          </Wrapper>
        );
      }

      if (type === "time_range") {
        const startField = props?.startField;
        const endField = props?.endField;

        return (
          <Wrapper key={idx}>
            {label ? (
              <p className="capitalize font12 mb-1">
                {label} <RequiredIndicator required={props?.required} />
                <InfoTooltip description={item?.description} />
              </p>
            ) : null}
            {error &&
              error[field_name] &&
              typeof error[field_name] === "string" && (
                <ErrorSlate message={error[field_name] as string} />
              )}
            <TimePicker.RangePicker
              size="middle"
              style={{ width: "100%" }}
              format="HH:mm"
              placeholder={["Start time", "End time"]}
              value={
                startField && endField && form[startField] && form[endField]
                  ? [
                      dayjs(form[startField], "HH:mm:ss"),
                      dayjs(form[endField], "HH:mm:ss"),
                    ]
                  : form[field_name as keyof typeof field_name] &&
                      Array.isArray(form[field_name as keyof typeof field_name])
                    ? [
                        dayjs(form[field_name][0], "HH:mm:ss"),
                        dayjs(form[field_name][1], "HH:mm:ss"),
                      ]
                    : null
              }
              onChange={(values) => {
                if (updateFormValues && startField && endField) {
                  updateFormValues({
                    [startField]: values
                      ? `${values[0]?.format("HH:mm")}:00`
                      : null,
                    [endField]: values
                      ? `${values[1]?.format("HH:mm")}:00`
                      : null,
                  });
                } else if (updateFormValue) {
                  const timeRange = values
                    ? [
                        `${values[0]?.format("HH:mm")}:00`,
                        `${values[1]?.format("HH:mm")}:00`,
                      ]
                    : null;
                  updateFormValue(field_name, timeRange);
                }
              }}
              {...props}
            />
          </Wrapper>
        );
      }

      if (type === "radio") {
        return (
          <Wrapper key={idx}>
            {label ? (
              <p
                className="capitalize font12 mb-1"
                style={{ display: "inline-block", width: "80%" }}
              >
                {label} <RequiredIndicator required={props?.required} />
                <InfoTooltip description={item?.description} />
              </p>
            ) : null}
            {error &&
              error[field_name] &&
              typeof error[field_name] === "string" && (
                <ErrorSlate message={error[field_name] as string} />
              )}
            <div
              className="form-check"
              style={{ display: "inline-block", width: "20%" }}
            >
              {(options_name && options
                ? returnArray(options[options_name])
                : []
              ).map((item, idx) => {
                return (
                  <div key={idx} className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name={field_name}
                      id={item.id}
                      value={item.id}
                      checked={form[field_name] === item.id}
                      onChange={(event) => {
                        if (updateFormValue) {
                          updateFormValue(field_name, event.target.value);
                        }
                      }}
                    />
                    <label className="form-check-label" htmlFor={item.id}>
                      {item.name}
                    </label>
                  </div>
                );
              })}
            </div>
          </Wrapper>
        );
      }
      if (type === "checkbox") {
        return (
          <Wrapper
            key={idx}
            style={{
              display: "flex",
              flexWrap: "nowrap",
              gap: "0.8rem",
              alignItems: "center",
            }}
          >
            <Checkbox
              type="checkbox"
              id={field_name}
              name={field_name}
              value={form[field_name]}
              checked={form[field_name]}
              onChange={(event) => {
                if (updateFormValue) {
                  const checked = event.target.checked;
                  updateFormValue(field_name, checked);
                }
              }}
              required={props?.required}
            />
            {label ? (
              <label className="capitalize font12 m-0" htmlFor={field_name}>
                {label} <RequiredIndicator required={props?.required} />{" "}
                <InfoTooltip description={item?.description} />
              </label>
            ) : null}
            {error && typeof error[field_name] === "string" && (
              <ErrorSlate message={error[field_name] as string} />
            )}
          </Wrapper>
        );
      }
      if (type === "location") {
        return (
          <Wrapper key={idx} style={{ gridColumn: "span 2" }}>
            <MapFormComponent
              form={form}
              updateFormValues={updateFormValues}
              updateFormValue={updateFormValue}
              required={props?.required}
              error={error}
            />
          </Wrapper>
        );
      }
      if (type === "file") {
        return (
          <Wrapper key={idx}>
            {label ? (
              <p className="capitalize font12 mb-1">
                {label} <RequiredIndicator required={props?.required} />{" "}
                <InfoTooltip description={item?.description} />
              </p>
            ) : null}
            <FileInput
              size="sm"
              updateFormValue={updateFormValue}
              form={form}
              is_array={props?.multiple}
              field_name={field_name}
              placeholder={getPlaceholder()}
              nested_attachment={props?.nested_attachment}
              // value={form?.[field_name] || value}
              {...props}
            />
            {error &&
              error[field_name] &&
              typeof error[field_name] === "string" && (
                <ErrorSlate message={error[field_name] as string} />
              )}
          </Wrapper>
        );
      }
      if (type === "decimal") {
        return (
          <Wrapper key={idx}>
            {label ? (
              <p className="capitalize font12 mb-1">
                {label} <RequiredIndicator required={props?.required} />{" "}
                <InfoTooltip description={item?.description} />
              </p>
            ) : null}
            <CFormInput
              style={{ fontSize: 14 }}
              onChange={onInputChange}
              name={field_name}
              placeholder={getPlaceholder()}
              value={form?.[field_name]}
              type={"number"}
              step={props?.step || "any"}
              {...props}
            />
            {error &&
              error[field_name] &&
              typeof error[field_name] === "string" && (
                <ErrorSlate message={error[field_name] as string} />
              )}
          </Wrapper>
        );
      }

      if (type === "multi_number_input") {
        return (
          <Wrapper key={idx}>
            <MultiNumberInput
              label={label}
              parameters={props?.parameters || []}
              value={form?.[field_name] || {}}
              onChange={(value) => {
                if (updateFormValue) {
                  updateFormValue(field_name, value);
                }
              }}
              props={props}
            />
            {error &&
              error[field_name] &&
              typeof error[field_name] === "string" && (
                <ErrorSlate message={error[field_name] as string} />
              )}
          </Wrapper>
        );
      }

      if (type === "component") {
        return <div {...props}>{component}</div>;
      }

      return (
        <Wrapper key={idx}>
          {label ? (
            <p className="capitalize font12 mb-1">
              {label} <RequiredIndicator required={props?.required} />{" "}
              <InfoTooltip description={item?.description} />
            </p>
          ) : null}

          {multiple && submitType === "create" ? (
            (form?.[field_name] || [""]).map((value: any, idx: number) => {
              const handleActions = (action: "delete" | "add", idx: number) => {
                if (action === "delete") {
                  if (updateFormValue)
                    updateFormValue(
                      field_name,
                      form[field_name].filter(
                        (_item: any, id: number) => id !== idx,
                      ),
                    );
                }

                if (action === "add") {
                  if (updateFormValue)
                    updateFormValue(field_name, [
                      ...(form?.[field_name] || [""]),
                      "",
                    ]);
                }
              };
              return (
                <CInputGroup key={value}>
                  <CInputGroupText>
                    <CIcon icon={Assets[type || "text"]} />
                  </CInputGroupText>
                  {type === "textarea" ? (
                    <CFormTextarea
                      onChange={(event) => {
                        if (updateFormValue)
                          updateFormValue(field_name, [
                            ...(form?.[field_name] || [""]).map(
                              (fld: any, id: number) => {
                                if (id === idx) return event.target.value;
                                return fld;
                              },
                            ),
                          ]);
                      }}
                      placeholder={getPlaceholder()}
                      value={value}
                      {...props}
                    />
                  ) : (
                    <CFormInput
                      size="sm"
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/,/g, "");
                        if (rawValue === "" || !isNaN(Number(rawValue))) {
                          const numericValue =
                            rawValue === "" ? null : Number(rawValue);
                          // Directly update form with numeric value
                          if (updateFormValue) {
                            updateFormValue(field_name, numericValue);
                          }
                        }
                      }}
                      name={field_name}
                      placeholder={getPlaceholder()}
                      value={
                        type === "number" &&
                        normalValue !== null &&
                        normalValue !== undefined
                          ? Number(normalValue).toLocaleString()
                          : normalValue
                      }
                      type={type === "number" ? "text" : type || "text"}
                      {...props}
                    />
                  )}

                  {(form?.[field_name] || [""]).length > 1 ? (
                    <CButton onClick={() => handleActions("delete", idx)}>
                      <CIcon icon={Assets["delete"]} />
                    </CButton>
                  ) : null}
                  {idx + 1 === (form?.[field_name] || [""]).length ? (
                    <CButton onClick={() => handleActions("add", idx)}>
                      <CIcon icon={Assets["plus"]} />
                    </CButton>
                  ) : null}
                </CInputGroup>
              );
            })
          ) : (
            <div>
              {type === "textarea" ? (
                <CFormTextarea
                  style={{ fontSize: 14 }}
                  onChange={onInputChange}
                  name={field_name}
                  placeholder={getPlaceholder()}
                  value={form?.[field_name]}
                  type={type || "text"}
                  {...props}
                />
              ) : type === "password" ? (
                <CInputGroup style={{ position: "relative" }}>
                  <CFormInput
                    style={{
                      fontSize: 14,
                      paddingRight: "2.5rem",
                    }}
                    onChange={onInputChange}
                    name={field_name}
                    placeholder={getPlaceholder()}
                    value={form?.[field_name]}
                    type={
                      form[`${field_name}_showPassword`] ? "text" : "password"
                    }
                    {...props}
                  />
                  {form[`${field_name}_showPassword`] ? (
                    <VisibilityOffOutlined
                      style={{
                        position: "absolute",
                        right: "1rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 20,
                        cursor: "pointer",
                        color: "gray",
                        zIndex: 999,
                      }}
                      onClick={() =>
                        updateFormValue(
                          `${field_name}_showPassword`,
                          !form[`${field_name}_showPassword`],
                        )
                      }
                    />
                  ) : (
                    <VisibilityOutlined
                      style={{
                        position: "absolute",
                        right: "1rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 20,
                        cursor: "pointer",
                        color: "gray",
                        zIndex: 999,
                      }}
                      onClick={() =>
                        updateFormValue(
                          `${field_name}_showPassword`,
                          !form[`${field_name}_showPassword`],
                        )
                      }
                    />
                  )}
                </CInputGroup>
              ) : type === "number" ? (
                <CFormInput
                  size="sm"
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/,/g, "");
                    if (rawValue === "" || !isNaN(Number(rawValue))) {
                      const numericValue =
                        rawValue === "" ? null : Number(rawValue);
                      // Directly update form with numeric value
                      if (updateFormValue) {
                        updateFormValue(field_name, numericValue);
                      }
                    }
                  }}
                  name={field_name}
                  placeholder={getPlaceholder()}
                  value={
                    type === "number" &&
                    normalValue !== null &&
                    normalValue !== undefined
                      ? Number(normalValue).toLocaleString()
                      : normalValue
                  }
                  type="text"
                  {...props}
                />
              ) : (
                <InputWrapper $loading={props?.loading}>
                  <CFormInput
                    style={{
                      fontSize: 14,
                      paddingRight: props?.loading ? "2.5rem" : "0.75rem",
                    }}
                    onChange={onInputChange}
                    name={field_name}
                    placeholder={getPlaceholder()}
                    value={form?.[field_name]}
                    type={type || "text"}
                    {...props}
                  />
                  <div className="spinner-container">
                    <CSpinner size="sm" />
                  </div>
                </InputWrapper>
              )}
            </div>
          )}
          {error &&
            error[field_name] &&
            typeof error[field_name] === "string" && (
              <ErrorSlate message={error[field_name] as string} />
            )}
        </Wrapper>
      );
    });
  };

  const renderFields = (fields: TFieldConfig[]) => {
    return (
      <div className="row" style={{ display: "flex", flexWrap: "wrap" }}>
        {fields.map((field, index) => {
          const isWide = field.props?.wide;
          const isNewLine = field.props?.newLine;
          const isSmall = field.props?.smallWidth;
          const isVisible = field.visible;

          if (isVisible == false) return;
          return (
            <React.Fragment key={index}>
              {isNewLine && <div style={{ flexBasis: "100%", height: 0 }} />}
              <div
                className={
                  isWide
                    ? "col-sm-12"
                    : isSmall
                      ? "col-sm-4"
                      : "col-lg-6 col-md-6 col-sm-12"
                }
                style={{
                  marginBottom: "1rem",
                  flexGrow: isWide ? 1 : 0,
                  ...returnObject(isWide, {
                    flexBasis: "100%",
                    maxWidth: "100%",
                  }),
                }}
              >
                {actualFields([field])}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  if (sectionFields) {
    return sectionFields.map((item, idx) => {
      return (
        <div key={idx}>
          <h4 className="pt-3">{item.title}</h4>
          {renderFields(item.fields || [])}
        </div>
      );
    });
  } else if (fields) {
    return renderFields(fields);
  }
};

export const RequiredIndicator = ({ required }: { required?: boolean }) =>
  required ? <span className="text-danger">*</span> : null;

export const InfoTooltip = ({ description }: { description?: string }) => {
  if (!description) return null;

  return (
    <Tooltip title={description}>
      <InfoCircleOutlined
        style={{
          marginLeft: 6,
          fontSize: 12,
          color: "#888",
          cursor: "pointer",
        }}
      />
    </Tooltip>
  );
};

export default FormFields;

const SelectInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const InputWrapper = styled.div<{ $loading?: boolean }>`
  position: relative;
  width: 100%;

  .spinner-container {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    display: ${(props) => (props.$loading ? "block" : "none")};
    z-index: 5;
    pointer-events: none;
  }
`;
